import { ModalFooter, ModalFooterProps } from '@components/shared/modal-footer.component';
import { ModalHeader, ModalHeaderProps } from '@components/shared/modal-header.component';
import { cn } from '@lib/utils';
import * as Portal from '@radix-ui/react-portal';
import React, { PropsWithChildren, useEffect, useRef } from 'react';

export type { ModalFooterProps };

/**
 * Stack of currently open modals: when modals stack (e.g. a confirmation dialog
 * over a fullscreen modal), only the topmost one may react to Escape.
 */
const openModalStack: symbol[] = [];

type ModalProps = {
  centered?: boolean;
  contentClassname?: string;
  description?: string;
  footer?: ModalFooterProps;
  header?: Omit<ModalHeaderProps, 'onClose'>;
  height?: string;
  isOpen: boolean;
  minWidth?: string;
  onClose?: () => void;
  width?: string;
  maxWidth?: string;
};

export const Modal: React.FC<PropsWithChildren<ModalProps>> = (props) => {
  const {
    centered = false,
    children,
    contentClassname,
    description,
    footer,
    header,
    height,
    isOpen,
    minWidth = '34%',
    onClose,
    width = '66%',
    maxWidth = '92vw',
  } = props;

  const modalIdRef = useRef<symbol>(Symbol('modal'));
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const modalId = modalIdRef.current;
    openModalStack.push(modalId);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openModalStack.at(-1) === modalId) {
        onCloseRef.current?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      const stackIndex = openModalStack.indexOf(modalId);
      if (stackIndex !== -1) {
        openModalStack.splice(stackIndex, 1);
      }
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    /** This is useful to prevent layout shift when the modal is opened */
    if (isOpen) {
      // Store the current scroll position
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Prevent body scroll and maintain scrollbar gutter to prevent layout shift
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const headerProps = { ...header, onClose };

  return (
    <Portal.Root>
      <div
        className={cn(
          'fixed inset-0 z-50 flex h-full w-full justify-center bg-black/50 backdrop-blur-[1px]',
          centered ? 'items-center' : 'items-start',
        )}
        onMouseDown={onClose}
        style={centered ? undefined : { paddingTop: 'calc(4vh + var(--impersonation-banner-height, 0px))' }}
        tabIndex={-1}
      >
        <div
          aria-modal="true"
          aria-labelledby={header ? 'modal-title' : undefined}
          aria-describedby={description ? 'modal-description' : undefined}
          className="relative flex flex-col items-start rounded-lg bg-white shadow-xl"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          style={{
            height,
            maxHeight: 'calc(92vh - var(--impersonation-banner-height, 0px))',
            maxWidth,
            minHeight: '100px',
            minWidth,
            width,
          }}
          tabIndex={-1}
        >
          {(header || description) && (
            <section className="flex flex-col w-full gap-1">
              {header && <ModalHeader {...headerProps} />}
              {description && <p className="text-sm px-4 pt-2 text-gray-500">{description}</p>}
            </section>
          )}
          <div className={cn('w-full grow overflow-auto overflow-x-hidden p-4', contentClassname)}>{children}</div>
          {footer && <ModalFooter {...footer} />}
        </div>
      </div>
    </Portal.Root>
  );
};
